import React, { useState, useEffect } from "react";
import { X, Loader2, Sparkles } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

// Go Live v1.0: hanya DEVELOPMENT & PRODUCTION
const ENVS = ["PRODUCTION", "DEVELOPMENT"];

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

export default function EnvironmentFormDialog({ tenants = [], open, onClose, onGenerated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      tenant_id: tenants[0]?.tenant_id || "",
      environment: "PRODUCTION",
      app_url: "",
      api_url: "",
      app_name: "",
      tenant_domain: "",
    });
  }, [open, tenants]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.tenant_id) { toast.error("Pilih tenant dulu."); return; }
    setBusy(true);
    try {
      const payload = { tenant_id: form.tenant_id, environment: form.environment };
      if (form.app_url) payload.app_url = form.app_url;
      if (form.api_url) payload.api_url = form.api_url;
      if (form.app_name) payload.app_name = form.app_name;
      if (form.tenant_domain) payload.tenant_domain = form.tenant_domain;
      const r = await api.post("/platform/environments/generate", payload);
      toast.success(`Config v${r.data.environment.config_version} dibuat.`);
      onGenerated?.(r.data.environment);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal generate environment"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="env-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">Generate Environment Config</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Tenant *</label>
            <Select value={form.tenant_id} onValueChange={(v) => set({ tenant_id: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="env-form-tenant"><SelectValue placeholder="Pilih tenant…" /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id} className={ITEM_CLS}>{t.tenant_name} · {t.tenant_code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Environment</label>
            <Select value={form.environment} onValueChange={(v) => set({ environment: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="env-form-env"><SelectValue /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {ENVS.map((e) => (<SelectItem key={e} value={e} className={ITEM_CLS}>{e}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">App URL (opsional)</label>
            <Input value={form.app_url} onChange={(e) => set({ app_url: e.target.value })}
              placeholder="https://tokoanda.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="env-form-app-url" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">API URL (opsional)</label>
            <Input value={form.api_url} onChange={(e) => set({ api_url: e.target.value })}
              placeholder="https://tokoanda.com/api" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="env-form-api-url" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">App Name (opsional)</label>
            <Input value={form.app_name} onChange={(e) => set({ app_name: e.target.value })}
              placeholder="Toko Anda" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="env-form-app-name" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.tenant_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="env-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Generating…</> : <><Sparkles size={14} className="mr-1" /> Generate</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
